var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import clsx from 'clsx';
import FormControl from '../FormControl';
import Input from '../Input';
import Typography from '../Typography';
import Link from '../Link';
import uid from '../utils/uid';
import { withForwardedRef } from '../utils/helpers/reactHelpers';
import { KEY_CODE, TIME_VALUES } from '../utils/constants';
const OtpWrapper = styled.div `
  display: flex;

  &._ndsOtpInput--fullWidth {
    width: 100%;
    align-items: stretch;
  }
`;
const SingleCharInputWrapperMargin = (p) => `${p.theme.SizePaddingInputOtpSingleY} ${p.theme.SizePaddingInputOtpSingleX}`;
const SingleCharInputWrapper = styled.div `
  width: ${(p) => p.theme.SizeWidthInputOtpWrapper};
  height: ${(p) => p.theme.SizeWidthInputOtpWrapper};
  margin: ${SingleCharInputWrapperMargin};

  & ._ndsFormControl__label {
    display: none;
  }

  & input {
    text-align: center;
    width: ${(p) => p.theme.SizeWidthInputOtpSingle};
  }

  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }

  /* Chrome, Safari, Edge, Opera */
  & input::-webkit-outer-spin-button,
  & input::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  /* Firefox */
  & input[type='number'] {
    appearance: textfield;
  }

  &._ndsOtpInput__wrapper--fullWidth {
    width: ${(p) => {
    return 'calc(100%/' + p.length + ');';
}};
    flex-grow: 1;
    input {
      width: 100%;
    }
  }
`;
const ResendWrapper = styled.div `
  padding-top: 0.5rem;

  & h6 {
    color: #cccccc;
  }
`;
const StyledSingleInput = styled(Input) `
  margin: 0;
`;
const getMaskType = () => {
    return 'password';
};
const getUnmaskType = (isNum) => {
    return isNum ? 'number' : 'tel';
};
const SingleCharInput = (props) => {
    const { index, isNum, inputId, fullWidth, length } = props, others = __rest(props, ["index", "isNum", "inputId", "fullWidth", "length"]);
    const numValueLimits = isNum ? { min: 0, max: 9 } : { maxLength: 1 };
    const classes = clsx({ '_ndsOtpInput__wrapper--fullWidth': fullWidth === true });
    const inputIdentifier = `${inputId}_${index}`;
    return (React.createElement(SingleCharInputWrapper, { className: classes, length: length },
        React.createElement(Input, Object.assign({ label: "", type: getUnmaskType(isNum) }, numValueLimits, { required: true, id: inputIdentifier, "data-testid": inputIdentifier, name: inputIdentifier, fullWidth: fullWidth, autoComplete: "one-time-code" }, (isNum && {
            inputMode: 'numeric',
            pattern: '[0-9]*'
        }), others))));
};
const OtpInput = (props) => {
    const { label, value: valueProp, onChange, length, isNum, helpText, error, fullWidth, isInitialCounter, disabled, onResend, resendTimer: counterProp, className: classNameProp, id, singleInput, forwardedRef } = props, others = __rest(props, ["label", "value", "onChange", "length", "isNum", "helpText", "error", "fullWidth", "isInitialCounter", "disabled", "onResend", "resendTimer", "className", "id", "singleInput", "forwardedRef"]);
    const classes = clsx('_ndsOtpInput', { '_ndsOtpInput--number': isNum !== null }, { '_ndsOtpInput--text': isNum === null }, { '_ndsOtpInput--fullWidth': fullWidth === true }, classNameProp);
    const inputRefs = useRef(Array.from({ length }, () => createRef()));
    const [activeInput, setActiveInput] = useState(0);
    const [counter, setCounter] = useState(isInitialCounter ? counterProp || 60 : 0);
    const { current: isControlled } = React.useRef(valueProp != null);
    const [valueState, setValueState] = useState(isControlled ? valueProp : '');
    const focusInput = (index) => {
        setActiveInput(Math.max(Math.min(length - 1, index), 0));
    };
    const focusPrevInput = () => {
        focusInput(activeInput - 1);
    };
    const focusNextInput = () => {
        focusInput(activeInput + 1);
    };
    const getOtpValue = () => {
        return isControlled ? valueProp : valueState;
    };
    const getOtpAsArray = () => {
        return getOtpValue()
            .toString()
            .split('');
    };
    const handleOtpChange = (newValues) => {
        const newOtpValue = newValues.join('');
        const oldValues = getOtpAsArray();
        for (let pos = 0; pos < length; pos += 1) {
            if (newValues[pos] && newValues[pos] !== oldValues[pos]) {
                inputRefs.current[pos].type = getUnmaskType(isNum);
                setTimeout(() => {
                    const targetInput = inputRefs && inputRefs.current[pos];
                    if (targetInput) {
                        targetInput.type = getMaskType();
                    }
                }, TIME_VALUES.MASK_DELAY);
            }
        }
        if (!isControlled) {
            setValueState(newOtpValue);
        }
        if (onChange) {
            onChange(newOtpValue);
        }
    };
    const changeValueAtFocus = (value) => {
        const currentValue = getOtpAsArray();
        let newOtp;
        if (activeInput === 0 && value.length === length) {
            newOtp = value.toString().split('');
        }
        else {
            currentValue[activeInput] = value[0];
            newOtp = currentValue;
        }
        handleOtpChange(newOtp);
    };
    const handleOnChange = (e) => {
        changeValueAtFocus(e.target.value);
        focusNextInput();
    };
    const handleKeyDown = (e) => {
        if (e.keyCode === KEY_CODE.BACKSPACE || e.key === 'Backspace') {
            e.preventDefault();
            changeValueAtFocus('');
            focusPrevInput();
        }
        else if (e.keyCode === KEY_CODE.DELETE || e.key === 'Delete') {
            e.preventDefault();
            changeValueAtFocus('');
        }
    };
    const handleOnBlur = () => {
        setActiveInput(-1);
    };
    const handleOnPaste = (e) => {
        e.preventDefault();
        const otp = getOtpAsArray();
        const pastedData = e.clipboardData
            .getData('text/plain')
            .slice(0, length)
            .split('');
        for (let pos = 0; pos < length; pos += 1) {
            if (pos >= 0 && pastedData.length > 0) {
                otp[pos] = pastedData.shift();
            }
        }
        handleOtpChange(otp);
    };
    const handleResendClick = () => {
        setCounter(counterProp || 60);
        if (onResend) {
            onResend();
        }
    };
    const inputId = React.useMemo(() => id || uid('_ndsInput_'), [id]);
    const helpTextId = `help-text-${inputId}`;
    const inputs = [];
    const renderInputsPerChar = () => {
        const otpToRender = getOtpAsArray();
        for (let _i = 0; _i < length; _i += 1) {
            inputs.push(React.createElement(SingleCharInput, { key: _i, index: _i, isNum: isNum, value: otpToRender[_i] || '', inputId: inputId, onChange: handleOnChange, onFocus: () => setActiveInput(_i), onBlur: handleOnBlur, onKeyDown: handleKeyDown, onPaste: handleOnPaste, inputRef: (el) => {
                    inputRefs.current[_i] = el;
                    return inputRefs.current[_i];
                }, error: error, disabled: disabled, fullWidth: fullWidth, length: length }));
        }
        return inputs;
    };
    useEffect(() => {
        if (!singleInput && inputRefs.current && inputRefs.current[activeInput]) {
            inputRefs.current[activeInput].focus();
            inputRefs.current[activeInput].select();
        }
    }, [singleInput, activeInput]);
    useEffect(() => {
        const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
        return () => clearInterval(timer);
    }, [counter]);
    return (React.createElement("div", { "aria-describedby": helpTextId },
        React.createElement(FormControl, { label: label, helpText: helpText, error: error, required: true, fullWidth: fullWidth, helpTextId: helpTextId, ref: forwardedRef }, singleInput ? (React.createElement(StyledSingleInput, Object.assign({ label: "", type: isNum ? 'tel' : 'text', maxLength: length, required: true, id: inputId, name: inputId, fullWidth: fullWidth, error: error, autoComplete: "one-time-code", value: valueProp, onChange: onChange }, (isNum && {
            inputMode: 'numeric',
            pattern: '[0-9]*'
        }), others))) : (React.createElement(OtpWrapper, Object.assign({ className: classes, id: id }, others), renderInputsPerChar()))),
        React.createElement(ResendWrapper, null, counter === 0 ? (React.createElement(Link, { onClick: handleResendClick, "data-testid": "otp-resend" }, "Resend OTP")) : (React.createElement(Typography, { variant: "subhead1", "data-testid": "otp-counter" },
            "Resend OTP in ",
            counter,
            "s")))));
};
OtpInput.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    length: PropTypes.number.isRequired,
    isNum: PropTypes.bool,
    helpText: PropTypes.any,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    isInitialCounter: PropTypes.bool,
    disabled: PropTypes.bool,
    resendTimer: PropTypes.number,
    onResend: PropTypes.func,
    className: PropTypes.string,
    id: PropTypes.string,
    singleInput: PropTypes.bool
};
OtpInput.defaultProps = {
    value: undefined,
    onChange: undefined,
    isNum: false,
    helpText: undefined,
    error: false,
    fullWidth: false,
    isInitialCounter: false,
    disabled: false,
    resendTimer: undefined,
    onResend: undefined,
    className: undefined,
    id: undefined,
    singleInput: undefined
};
export { OtpInput as OtpInputRootComp };
export default withForwardedRef(OtpInput);
//# sourceMappingURL=OtpInput.js.map